import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import VideoCard from "@/components/base/VideoCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getCategoryById, getVideosByCategory } from "@/utils/videoData";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);
  const [activeSub, setActiveSub] = useState("全部");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setActiveSub("全部");
    setVisible(PAGE_SIZE);
  }, [categoryId]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [activeSub]);

  const allVideos = useMemo(() => (category ? getVideosByCategory(category.id) : []), [category]);

  const filtered = useMemo(() => {
    if (activeSub === "全部") return allVideos;
    return allVideos.filter((v) => v.tags.includes(activeSub));
  }, [allVideos, activeSub]);

  usePageMeta({
    title: category ? `${category.name} - 一起草视频` : "分类 - 一起草视频",
    description: category
      ? `探索一起草视频中关于“${category.name}”的高清视频资源，支持筛选与在线播放。`
      : "浏览一起草视频的各类高清视频资源。",
    keywords: category
      ? `一起草视频,${category.name},高清视频,在线观看`
      : "一起草视频,视频分类,高清视频",
  });

  if (!category) {
    return (
      <div className="px-4 md:px-6 py-16 max-w-3xl mx-auto">
        <EmptyState
          icon="ri-error-warning-line"
          title="该分类不存在"
          description="你访问的分类可能已被移除，返回首页看看其他精彩内容吧。"
          actionText="返回首页"
          actionLink="/"
        />
      </div>
    );
  }

  return (
    <div className="bg-background-50">
      <div className="bg-primary-950">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs md:text-sm text-primary-200/80 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-base" />
            </span>
            <span className="text-accent-400 whitespace-nowrap">{category.name}</span>
          </nav>
          <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 flex items-center gap-3">
            <span className="w-11 h-11 md:w-14 md:h-14 rounded-lg bg-accent-500 text-primary-950 flex items-center justify-center shrink-0">
              <i className={`${category.icon} text-2xl md:text-3xl`} />
            </span>
            {category.name}
          </h1>
          <p className="mt-4 text-sm md:text-base text-primary-100/85 max-w-2xl leading-relaxed">
            {category.description}
          </p>
        </div>
      </div>

      <div className="px-4 md:px-6 py-8 md:py-10">
        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1 mb-6">
          {category.subCategories.map((sub) => {
            const active = activeSub === sub;
            return (
              <button
                key={sub}
                type="button"
                onClick={() => setActiveSub(sub)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                }`}
              >
                {sub}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 个视频
          </p>
          <Link
            to={`/list/${category.id}`}
            className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-filter-3-line text-base" />
            </span>
            多维筛选
          </Link>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            icon="ri-movie-2-line"
            title="该分类下暂无视频"
            description="换个标签或返回首页看看其他内容吧。"
            actionText="返回首页"
            actionLink="/"
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {filtered.slice(0, visible).map((video, idx) => (
                <VideoCard key={video.id} video={video} index={idx} />
              ))}
            </div>
            {visible < filtered.length && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 h-11 px-8 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line text-lg" />
                  </span>
                  加载更多
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}